import BaseClient from './BaseClient.js';
import fetchApi, { populateHeaders } from './utils/fetchApi.js';
import parseApiResponse, { parseRawApiResponse, } from './utils/parseApiResponse.js';
import isNode from 'detect-node';
const CONTENT_TYPE_HEADER = 'Content-Type';
const NO_OP_PROGRESS_HANDLER = () => { };
export default class ServiceClient extends BaseClient {
    /**
     * Execute service operation specified by endpoint
     */
    async exec(endpoint, params) {
        const headers = {
            ...this.globalHeaders,
            [CONTENT_TYPE_HEADER]: 'application/json',
        };
        const url = this.getUrl(endpoint);
        const onApiError = this.getOption('onApiError');
        const response = await fetchApi({
            ...this.requestContext,
            url,
            headers,
            body: JSON.stringify(params),
            method: 'POST',
        });
        return await parseApiResponse(response, onApiError);
    }
    async fetch(endpoint, { method, headers, body, }) {
        const url = this.getUrl(endpoint);
        const finalHeaders = {
            ...this.globalHeaders,
            ...headers,
        };
        return await fetchApi({
            ...this.requestContext,
            url,
            method,
            headers: finalHeaders,
            body,
        });
    }
    async upload(endpoint, data, onProgress, signal) {
        const method = 'POST';
        const requestHeaders = new Headers(this.globalHeaders);
        populateHeaders(requestHeaders, this.requestContext);
        if (this.uploader) {
            const response = await this.uploader({
                url: this.getUrl(endpoint),
                method,
                data,
                headers: requestHeaders,
                onProgress: onProgress || NO_OP_PROGRESS_HANDLER,
                signal,
            });
            return parseRawApiResponse(response.statusCode >= 200 && response.statusCode < 300, response.statusText, () => JSON.parse(response.body));
        }
        let formData = new FormData();
        data.forEach(({ name, value, filename }) => {
            if (filename) {
                formData.append(name, value, filename);
            }
            else {
                formData.append(name, value);
            }
        });
        if (!isNode) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const handleFinished = (error) => {
                    try {
                        if (error) {
                            throw error;
                        }
                        resolve(parseRawApiResponse(xhr.status === 200, xhr.statusText, () => Promise.resolve(JSON.parse(xhr.responseText))));
                    }
                    catch (e) {
                        reject(e);
                    }
                };
                signal?.addEventListener('abort', () => {
                    xhr.abort();
                });
                xhr.upload.addEventListener('progress', ({ loaded, total }) => {
                    onProgress && onProgress({ loaded, total });
                }, false);
                xhr.addEventListener('load', () => {
                    if (xhr.readyState === xhr.DONE) {
                        handleFinished();
                    }
                }, false);
                xhr.addEventListener('error', () => {
                    handleFinished(new Error('Connection was interrupted'));
                }, false);
                xhr.addEventListener('abort', () => {
                    handleFinished(new Error('Upload was aborted'));
                }, false);
                xhr.open(method, this.getUrl(endpoint));
                requestHeaders.forEach((val, key) => {
                    xhr.setRequestHeader(key, val);
                });
                xhr.send(formData);
            });
        }
        else {
            const response = await this.fetch(endpoint, {
                method,
                body: formData,
                // AbortSignal is not part of fetch standard, but available in node-fetch
                ...{ signal },
            });
            return parseApiResponse(response, this.getOption('onApiError'));
        }
    }
    getUrl(endpoint) {
        return this.buildUrl(`/api/${endpoint}`);
    }
}
